import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Mic, 
  Send, 
  Loader2,
  Play,
  CheckCircle2,
  AlertCircle,
  Star,
  Trophy,
  RotateCcw,
  Clock,
  Target,
  TrendingUp,
  Lock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AISimulationPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSimulation, setActiveSimulation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [sending, setSending] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [starting, setStarting] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/ai-simulations/status`);
      setStatus(res.data);
    } catch (error) {
      console.error("Error fetching status:", error);
      toast.error("Error al cargar estado de simulaciones");
    } finally {
      setLoading(false);
    }
  };

  const startSimulation = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API}/ai-simulations/start`, {});
      setActiveSimulation(res.data.simulation_id);
      setMessages(res.data.messages || []);
      setEvaluation(null);
      toast.success(res.data.status === "resumed" ? "Continuando simulación" : "Simulación iniciada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar simulación");
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async () => {
    if (!userInput.trim() || sending || !activeSimulation) return;

    const myMessage = userInput.trim();
    setUserInput("");
    setSending(true);

    // Add user message optimistically
    setMessages(prev => [...prev, {
      role: "user",
      content: myMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const res = await axios.post(`${API}/ai-simulations/${activeSimulation}/message`, {
        content: myMessage
      });

      // Add AI response
      setMessages(prev => [...prev, res.data.message]);

      if (res.data.status === "completed") {
        setEvaluation(res.data.evaluation);
        setActiveSimulation(null);
        fetchStatus(); // Refresh status
        toast.success("Simulación completada");
      }
    } catch (error) {
      toast.error("Error al enviar respuesta");
      // Remove optimistic message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const endSimulationEarly = async () => {
    if (!activeSimulation) return;
    
    try {
      const res = await axios.post(`${API}/ai-simulations/${activeSimulation}/end`);
      setEvaluation(res.data.evaluation);
      setActiveSimulation(null);
      fetchStatus();
      toast.success("Simulación finalizada");
    } catch (error) {
      toast.error("Error al finalizar");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="ai-simulation-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mic className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-navy-900">
            Simulador de Entrevista
          </h1>
          <p className="text-navy-500 mt-2">
            Practica tu entrevista consular con inteligencia artificial
          </p>
        </motion.div>

        {/* Status Card */}
        {!activeSimulation && !evaluation && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-navy-100 p-6 mb-6"
          >
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-purple-50 rounded-xl">
                <Target className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-navy-900">{status?.allowed || 0}</p>
                <p className="text-sm text-navy-500">Permitidos</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-navy-900">{status?.used || 0}</p>
                <p className="text-sm text-navy-500">Completados</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-xl">
                <Play className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-navy-900">{status?.remaining || 0}</p>
                <p className="text-sm text-navy-500">Disponibles</p>
              </div>
            </div>

            {!status?.ds160_completed ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                <Lock className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800">DS-160 Requerido</p>
                  <p className="text-sm text-amber-700">
                    Completa tu formulario DS-160 para desbloquear los simulacros
                  </p>
                </div>
              </div>
            ) : status?.remaining === 0 ? (
              <div className="bg-navy-50 border border-navy-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-navy-600" />
                <div>
                  <p className="font-semibold text-navy-800">Sin simulacros disponibles</p>
                  <p className="text-sm text-navy-600">
                    Has usado todos los simulacros de tu plan {status?.plan}
                  </p>
                </div>
              </div>
            ) : (
              <Button
                onClick={startSimulation}
                disabled={starting}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 h-14 text-lg"
                data-testid="start-simulation-btn"
              >
                {starting ? (
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                ) : (
                  <Play className="w-6 h-6 mr-2" />
                )}
                Iniciar Simulación
              </Button>
            )}

            {/* History */}
            {status?.history && status.history.length > 0 && (
              <div className="mt-6 pt-6 border-t border-navy-100">
                <h3 className="font-semibold text-navy-900 mb-3">Historial</h3>
                <div className="space-y-2">
                  {status.history.map((sim, index) => (
                    <div key={sim.id || index} className="flex items-center justify-between p-3 bg-navy-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className={`w-4 h-4 ${sim.status === "completed" ? "text-emerald-600" : "text-amber-600"}`} />
                        <span className="text-sm text-navy-700">
                          {new Date(sim.created_at).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      {sim.score && (
                        <span className="text-sm font-semibold text-purple-600">
                          {sim.score}/100
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Active Simulation */}
        {activeSimulation && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl border border-navy-100 overflow-hidden"
          >
            {/* Simulation Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Mic className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold">Entrevista en Curso</p>
                  <p className="text-sm text-purple-200">Responde como lo harías en la embajada</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={endSimulationEarly}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                Terminar
              </Button>
            </div>

            {/* Messages */}
            <div className="h-[400px] overflow-y-auto p-4 bg-navy-50">
              <div className="space-y-4">
                {messages.map((msg, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.role === "user"
                          ? "bg-purple-600 text-white rounded-br-md"
                          : "bg-white text-navy-900 rounded-bl-md border border-navy-200"
                      }`}
                    >
                      {msg.role === "assistant" && (
                        <p className="text-xs font-semibold text-purple-600 mb-1">
                          Oficial Consular
                        </p>
                      )}
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </motion.div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-white rounded-2xl px-4 py-3 border border-navy-200">
                      <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Input */}
            <div className="p-4 border-t border-navy-100 bg-white">
              <div className="flex gap-3">
                <Textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Escribe tu respuesta..."
                  className="resize-none"
                  rows={2}
                  disabled={sending}
                  data-testid="simulation-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!userInput.trim() || sending}
                  className="bg-purple-600 hover:bg-purple-700 px-6"
                  data-testid="simulation-send-btn"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Evaluation Results */}
        {evaluation && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-navy-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white text-center">
              <Trophy className="w-16 h-16 mx-auto mb-3" />
              <h2 className="text-2xl font-bold">Simulación Completada</h2>
              <div className="mt-4">
                <span className="text-6xl font-bold">{evaluation.score}</span>
                <span className="text-2xl">/100</span>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Probability */}
              <div className={`p-4 rounded-xl text-center ${
                evaluation.approval_probability === "Alta" ? "bg-emerald-50" :
                evaluation.approval_probability === "Media" ? "bg-amber-50" : "bg-red-50"
              }`}>
                <p className="text-sm text-navy-600 mb-1">Probabilidad de Aprobación</p>
                <p className={`text-2xl font-bold ${
                  evaluation.approval_probability === "Alta" ? "text-emerald-600" :
                  evaluation.approval_probability === "Media" ? "text-amber-600" : "text-red-600"
                }`}>
                  {evaluation.approval_probability}
                </p>
              </div>

              {/* Strengths */}
              {evaluation.strengths?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-navy-900 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    Puntos Fuertes
                  </h3>
                  <ul className="space-y-2">
                    {evaluation.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-navy-700">
                        <span className="text-emerald-500 mt-1">•</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {evaluation.improvements?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-navy-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                    Áreas de Mejora
                  </h3>
                  <ul className="space-y-2">
                    {evaluation.improvements.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-navy-700">
                        <span className="text-amber-500 mt-1">•</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {evaluation.recommendations?.length > 0 && (
                <div className="bg-purple-50 rounded-xl p-4">
                  <h3 className="font-semibold text-purple-900 mb-2">Recomendaciones</h3>
                  <ul className="space-y-1">
                    {evaluation.recommendations.map((r, i) => (
                      <li key={i} className="text-sm text-purple-700">→ {r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => {
                    setEvaluation(null);
                    fetchStatus();
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Volver
                </Button>
                {status?.remaining > 0 && (
                  <Button
                    onClick={startSimulation}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Nueva Simulación
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AISimulationPage;
